import { Box_t, Pallet_t, Layout_t, BoxPosition_t, applyLabelSpins, LayoutTransform } from "blackbox";
import { PackResult } from "libapi";
import { v4 as uuidv4 } from 'uuid';
import { useState } from "react";

import { SelectTrigger } from "../../../Layout";
import Button from "../../../PendantButton";
import { wrapChangeEventString, COLOR, isWebDeployment } from "../../../shared/shared";
import { LayoutTypes, generateAutoLayout } from "../../layout_utils";

export interface AutoLayoutProps {
	allBoxes: Box_t[];
	gapDistance: number /* gap distance millimeters */;
	allPallets: Pallet_t[];
	cancelAutoLayout: () => void;
	boxIndex: number;
	palletIndex: number;
	modelSize: number;
	currentLayoutName: string;
	name: string;
	updateLayout: (layout: Layout_t, pallet_index: number) => void;
	triggerSelect: SelectTrigger;
	packFunc: (
		palletLength: number,
		palletWidth: number,
		boxLength: number,
		boxWidth: number
	) => Promise<PackResult[]>;
	layoutId?: string;
  overhang: number;
}

function applyGapBoxes(pAllBoxes: Box_t[], pGap: number, pBoxIndex: number) {
	return pAllBoxes.map(function (pBox: Box_t, pIndex: number) {
		if (pIndex !== pBoxIndex) return pBox;
		const lBox = { ...pBox };
		const lDims = { ...lBox.dimensions };
		lDims.width += pGap;
		lDims.length += pGap;
		lBox.dimensions = lDims;
		return lBox;
	});
}

function removeGapShiftPositions(
	pBoxPositions: BoxPosition_t[],
	pPallet: Pallet_t,
	pGapDistance: number
) {
	const lWidthShift = pGapDistance / (2 * pPallet.Dimensions.width),
		lLengthShift = pGapDistance / (2 * pPallet.Dimensions.length);

	return pBoxPositions.map(function (pBoxPosition: BoxPosition_t) {
		const lCp = { ...pBoxPosition.position };
		lCp.x += lWidthShift;
		lCp.y += lLengthShift;
		return { ...pBoxPosition, position: lCp };
	});
}

export function AutoLayout({
	allBoxes,
	allPallets,
	boxIndex,
	palletIndex,
	updateLayout,
	currentLayoutName,
	gapDistance,
	cancelAutoLayout,
	packFunc,
	triggerSelect,
	layoutId,
  overhang
}: AutoLayoutProps) {
	const [layoutType, setLayoutType] = useState<LayoutTypes>(
		LayoutTypes.Optimized
	),
		handleType = wrapChangeEventString((k: string) => {
			setLayoutType(k as LayoutTypes);
		}),
		handleGenerate = function () {
			const lGappedBoxes = applyGapBoxes(allBoxes, gapDistance, boxIndex);
			generateAutoLayout(
				allPallets,
				lGappedBoxes,
				boxIndex,
				palletIndex,
				gapDistance,
				layoutType,
				packFunc,
        overhang
			)
				.then(bps => {
					const l: Layout_t = {
						name: currentLayoutName,
						id: layoutId || String(uuidv4()),
						box_id: '',
						boxPositions: removeGapShiftPositions(
							bps,
							allPallets[palletIndex],
							gapDistance
						),
						gapDistance,
            overhang
					};

          // apply label spins.
      
          const pallet: any = { ...allPallets[palletIndex]};
          pallet.Layouts = [{...l}];
      
          // Transform to robot space
          let targetLayout = LayoutTransform.Transform(
            LayoutTransform.TransformType.ReflectGlobalY,
            pallet,
            allBoxes,
            0,
          );
          applyLabelSpins(allBoxes, targetLayout, pallet);
          pallet.Layouts = [targetLayout];
          targetLayout = LayoutTransform.Transform(
            LayoutTransform.TransformType.ReflectGlobalX,
            pallet,
            allBoxes,
            0,
          );
				  applyLabelSpins(allBoxes, targetLayout, pallet);
    
          // only apply the label spins from the transformed layout
          l.boxPositions = l.boxPositions.map((boxPos, i) => {
            const transformedBoxPosition = targetLayout.boxPositions[i];
            return {
              ...boxPos,
              spin: transformedBoxPosition.spin,
              rotated: transformedBoxPosition.rotated
            }
          })
					updateLayout(l, palletIndex);
				})
				.catch(console.log);
		},
		lTriggerAutoSelect = function (pEvent: React.SyntheticEvent) {
			if (!triggerSelect) return;
			pEvent.preventDefault();
			const lLayoutTypes = Object.keys(LayoutTypes).map(
				pType => (LayoutTypes as any)[pType]
			);
			triggerSelect(
				'Select Auto Layout Type',
				lLayoutTypes,
				function (pNewTypeIndex: number) {
					setLayoutType(lLayoutTypes[pNewTypeIndex]);
				},
				lLayoutTypes.indexOf(layoutType)
			);
		};

	return (
		<div className="AutoLayout">
			<div></div>
			<div className="flex-center full-size">
				<div className="flex-center full-size">
					<div className="padding-right">
						<span className="large-font">Layout Type:</span>
					</div>
					<div className="padding-left">
						<select
							value={layoutType}
							onChange={handleType}
							onMouseDown={lTriggerAutoSelect}
						>
							{Object.keys(LayoutTypes).map((k: string, i: number) => {
								const t = (LayoutTypes as any)[k];
								return (
									<option key={i} value={t}>
										{t}
									</option>
								);
							})}
						</select>
					</div>
				</div>
			</div>
			<div className="full-size flex-col flex-start align-center">
				<Button
					pClass="width-30"
					pAction={handleGenerate}
					pNormalColorOverride={COLOR.LIGHT_BLUE}
					pClickColorOverride={COLOR.LIGHT_BLUE}
				>
					<span>Generate Layout</span>
				</Button>
				<div className="padding-top"> </div>
				<Button pClass="width-30" pAction={cancelAutoLayout} pClickRed={true}>
					<span>Cancel</span>
				</Button>
			</div>
		</div>
	);
}